import type { CSSProperties } from 'react';
import { useScrollReveal } from '../../hooks/useScrollReveal';
import { useSectionDeviceRotation } from '../../hooks/useSectionDeviceRotation';
import type { ApplicationPlatform } from '../../data/application';
import { DeviceFrame } from './DeviceFrame';
import { PlatformDownloadButton } from './PlatformDownloadButton';

interface PlatformSectionProps {
  platform: ApplicationPlatform;
  index: number;
}

export function PlatformSection({ platform, index }: PlatformSectionProps) {
  const { ref, visible } = useScrollReveal<HTMLElement>(0.18);
  const reversed = index % 2 === 1;
  const isPhone = platform.device === 'iphone' || platform.device === 'android';
  const hasFramelessImage = 'image' in platform && Boolean(platform.image);

  useSectionDeviceRotation(ref, reversed);

  return (
    <section
      ref={ref}
      id={platform.id}
      className={[
        'platform-section',
        reversed ? 'platform-section--reverse' : '',
        !isPhone ? 'platform-section--landscape' : '',
        hasFramelessImage ? 'platform-section--frameless' : '',
        visible ? 'is-visible' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      style={{ '--platform-accent': platform.accent } as CSSProperties}
    >
      <div className="container">
        <div className="platform-section__grid">
          <div
            className={[
              'platform-section__device',
              !isPhone ? 'platform-section__device--landscape' : '',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            <span className="platform-section__badge">{platform.platform}</span>
            <div className="platform-section__device-stage">
              {hasFramelessImage ? (
                <img
                  src={platform.image}
                  alt={platform.imageAlt}
                  className="platform-section__image"
                  loading="lazy"
                  decoding="async"
                />
              ) : (
                <DeviceFrame type={platform.device} accent={platform.accent} label={`${platform.title} device`} />
              )}
            </div>
          </div>

          <div className="platform-section__copy reveal-item">
            <span className="platform-section__index">0{index + 1}</span>
            <p className="platform-section__eyebrow">{platform.headline}</p>
            <h2 className="platform-section__title">{platform.title}</h2>
            <p className="platform-section__description paragraph-02 text-dark-grey-two">{platform.description}</p>
            <ul className="platform-section__bullets">
              {platform.bullets.map((bullet) => (
                <li key={bullet}>{bullet}</li>
              ))}
            </ul>
            <PlatformDownloadButton platform={platform} />
          </div>
        </div>
      </div>
    </section>
  );
}
